import React, { useState } from "react";

import type { LedgerImportAction, LedgerImportRowDTO } from "../../data/ledgerImportApi";
import {
  ACTION_LABEL,
  PUSHED_ACTION_LABEL,
  ACTION_STYLE,
  ROW_STATUS_LABEL,
  ROW_STATUS_STYLE,
  cellText,
  fieldLabel,
  groupByAction,
  notAddedRows,
} from "../../domain/ledgerImport";
import { ChevronDownIcon, ChevronRightIcon } from "../../components/icons";
import { cn } from "../../components/cn";

/**
 * Every row of the sheet, grouped by what would happen to it.
 *
 * Grouped rather than left in sheet order, because the sheet order is 128 rows
 * of which 125 are noise. Each row still leads with its own line number so it
 * can be found in Excel — the grouping is for reading, the number is for fixing.
 *
 * Unchanged starts collapsed and the rest start open. Collapsing a problem
 * would be hiding it; collapsing 125 rows that do nothing is the whole point.
 *
 * Still grouped by ACTION after a push, not by outcome. The CA is reading this
 * against the sheet they edited, and re-sorting the list under them at the
 * moment they most want to check a specific line would break that. The outcome
 * rides on each row instead.
 *
 * Except the rows that did not go through. Those are listed once, in their own
 * block above (LedgerImportRejected), and left out here, so "Added 148" means
 * 148 ledgers were added (ZOUR-257).
 */

function RowLine({ row, showOutcome }: { row: LedgerImportRowDTO; showOutcome: boolean }) {
  const changes = Object.entries(row.changes ?? {});

  return (
    <li className="flex flex-col gap-1 rounded-md border p-3">
      <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
        <span className="shrink-0 font-mono text-xs text-muted-foreground">
          {/* The line in their own spreadsheet, so a fix is a click away in Excel. */}
          Row {row.sheet_row_no}
        </span>
        <span className="text-sm font-medium">{row.account_name ?? "(no name)"}</span>
        <span className="text-xs text-muted-foreground">{row.account_type_label}</span>
        {row.parent_name !== null && (
          <span className="text-xs text-muted-foreground">under {row.parent_name}</span>
        )}
        {showOutcome && (
          <span
            className={cn(
              "ml-auto shrink-0 rounded-sm px-1.5 py-0.5 text-[11px] font-medium",
              ROW_STATUS_STYLE[row.status]
            )}
          >
            {ROW_STATUS_LABEL[row.status]}
          </span>
        )}
      </div>

      {changes.length > 0 && (
        <ul className="flex flex-col gap-0.5 pl-1">
          {changes.map(([field, change]) => (
            <li key={field} className="flex flex-wrap items-baseline gap-1.5 text-xs">
              <span className="text-muted-foreground">{fieldLabel(field)}</span>
              <span className="line-through opacity-60">{cellText(change.from)}</span>
              <span aria-hidden="true" className="text-muted-foreground">
                →
              </span>
              <span className="font-medium">{cellText(change.to)}</span>
            </li>
          ))}
        </ul>
      )}

      {row.issues.map((issue, index) => (
        <p
          key={index}
          className={cn(
            "text-xs",
            issue.severity === "error"
              ? "text-amber-700 dark:text-amber-400"
              : "text-muted-foreground"
          )}
        >
          <span className="font-medium">{issue.field}:</span> {issue.message}
        </p>
      ))}
    </li>
  );
}

function Group({
  action,
  rows,
  open,
  onToggle,
  showOutcomes,
}: {
  action: LedgerImportAction;
  rows: LedgerImportRowDTO[];
  open: boolean;
  onToggle: () => void;
  showOutcomes: boolean;
}) {
  const Chevron = open ? ChevronDownIcon : ChevronRightIcon;

  return (
    <section className="flex flex-col gap-2">
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={open}
        className="flex items-center gap-1.5 text-left text-sm font-medium"
      >
        <Chevron className="size-4 text-muted-foreground" />
        <span
          className={cn("rounded-sm px-1.5 py-0.5 text-xs font-medium", ACTION_STYLE[action])}
        >
          {showOutcomes ? PUSHED_ACTION_LABEL[action] : ACTION_LABEL[action]}
        </span>
        <span className="text-muted-foreground">{rows.length}</span>
      </button>

      {open && (
        <ul className="flex max-h-[28rem] flex-col gap-2 overflow-y-auto">
          {rows.map((row) => (
            <RowLine key={row.id} row={row} showOutcome={showOutcomes} />
          ))}
        </ul>
      )}
    </section>
  );
}

export function LedgerImportRows({
  rows,
  showOutcomes = false,
}: {
  rows: LedgerImportRowDTO[];
  /** Off until a push has run: every row is `pending` before that, and saying so a hundred times is noise. */
  showOutcomes?: boolean;
}) {
  const [collapsed, setCollapsed] = useState<Set<LedgerImportAction>>(
    () => new Set<LedgerImportAction>(["unchanged"])
  );

  function toggle(action: LedgerImportAction) {
    setCollapsed((previous) => {
      const next = new Set(previous);
      if (next.has(action)) next.delete(action);
      else next.add(action);
      return next;
    });
  }

  const notAdded = new Set(showOutcomes ? notAddedRows(rows).map((row) => row.id) : []);
  const listed = rows.filter((row) => !notAdded.has(row.id));

  return (
    <div className="flex flex-col gap-4 border-t pt-4">
      {groupByAction(listed).map((group) => (
        <Group
          key={group.action}
          action={group.action}
          rows={group.rows}
          open={!collapsed.has(group.action)}
          onToggle={() => toggle(group.action)}
          showOutcomes={showOutcomes}
        />
      ))}
    </div>
  );
}
